import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { setIntegrationState } from '../../features/integrations/integrationsSlice';
import { IntegrateModal } from './IntegrateModal';
import { DisconnectModal } from './DisconnectModal';
import { FiChevronLeft } from 'react-icons/fi';
import integrationdetails from '../../assets/integration_details.png';
import card1 from '../../assets/card1.png';
import card2 from '../../assets/card2.png';
import card3 from '../../assets/card3.png';
import card4 from '../../assets/card4.png';
import IntegrationForm from './IntegrationForm';
import { ToastContainer, toast } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from '../../utils/constants';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const IntegrationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const integration = useSelector((state: RootState) =>
    state.integrations.items.find((i) => i.id === Number(id))
  );

  const [showIntegrate, setShowIntegrate] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [loading, setLoading] = useState(false);

  const isConnected = integration?.status === 'CONNECTED';

  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleConnectIntegration = async () => {
    if (!integration) return;

    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/integrations/${integration.providerKey}/connect`, {
        method: 'POST',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to connect integration: ${response.status}`);
      }

      const data = await response.json();

      // Check for both OK and ACCEPTED status
      if (data.status === 'OK' || data.status === 'ACCEPTED') {
        dispatch(
          setIntegrationState({
            id: integration.id,
            integrated: true,
            status: 'CONNECTED'
          })
        );
        toast.success(`${integration.name} integrated successfully 🎉`);
        setShowIntegrate(false);
      } else {
        throw new Error(data.message || 'Failed to connect integration');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to connect integration');
      // console.error('Connect integration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectIntegration = async () => {
    if (!integration) return;

    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/integrations/${integration.providerKey}/disconnect`, {
        method: 'POST',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to disconnect integration: ${response.status}`);
      }

      const data = await response.json();

      // Check for both OK and ACCEPTED status
      if (data.status === 'OK' || data.status === 'ACCEPTED') {
        dispatch(
          setIntegrationState({
            id: integration.id,
            integrated: false,
            status: 'DISCONNECTED'
          })
        );
        toast.info(`${integration.name} disconnected successfully`);
        setShowDisconnect(false);
      } else {
        throw new Error(data.message || 'Failed to disconnect integration');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to disconnect integration');
      // console.error('Disconnect integration error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!integration) {
    return (
      <div className='p-6'>
        <h2 className='text-xl font-bold'>Integration not found</h2>
        <button
          onClick={() => navigate('/settings')}
          className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
          style={{ color: '#00899E', fontSize: '14px' }}
        >
          <FiChevronLeftComponent className='mr-1' /> Back to Integrations
        </button>
      </div>
    );
  }

  const handleIntegrate = () => {
    setShowIntegrate(true);
  };

  const handleDisconnect = () => {
    setShowDisconnect(true);
  };

  return (
    <div className='p-2'>
      <button
        onClick={() => navigate('/settings')}
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Integrations
      </button>
      <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>
        Integration Details
      </h2>
      <p className='text-[#657584] text-[16px] mb-6'>
        Power up your workflow with smart integrations
      </p>

      <div className='border rounded p-4 flex items-center justify-between bg-white shadow-sm'>
        <div className='flex items-center gap-3'>
          <img src={integration.logo} alt={integration.name} className='h-10' />
          <div>
            <h3 className='font-semibold text-[20px] text-[#3F5265]'>
              {integration.name}
            </h3>
            <p className='text-[#657584] text-[16px]'>
              Bring your favorite tools together to simplify tasks, automate
              updates, and get more done.
            </p>
            <div className='mt-2'>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${isConnected
                ? 'bg-[#ECFDF5] text-[#07A26F]'
                : 'bg-gray-100 text-gray-600'
                }`}>
                {isConnected ? '● Connected' : '● Disconnected'}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={isConnected ? handleDisconnect : handleIntegrate}
          disabled={loading}
          className={`${isConnected
            ? 'border border-[#FF5722] bg-white text-[#FF5722] hover:bg-orange-100'
            : 'bg-[#FF5722] text-white hover:bg-[#e04a1c]'
            } px-6 py-2 rounded text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isConnected ? 'Remove Integration' : 'Integrate Now'}
        </button>
      </div>

      {isConnected ? (
        <IntegrationForm integrationName={integration.name} />
      ) : (
        <div className='mt-6 border rounded bg-white shadow-sm overflow-hidden p-8'>
          <div className='relative'>
            <img
              src={integrationdetails}
              alt='Integration Preview'
              className='w-full h-[20rem] object-cover rounded-xl'
            />
            <div className='absolute inset-0 bg-black bg-opacity-60 rounded-xl'></div>
            <div className='absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6'>
              <h3 className='text-[36px] font-bold mb-2 w-[40%] leading-[45px]'>
                Send smarter campaigns with {integration.name}
              </h3>
              <p className='leading-[1.5rem] max-w-xl text-[16px] font-normal w-[35%]'>
                Create personalized messages and campaigns using the data pulled
                directly from your connected tools.
              </p>

            </div>
          </div>
          <div className='mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
            {[card1, card2, card3, card4].map((card, idx) => (
              <div
                key={idx}
                className='bg-white rounded-xl shadow-md overflow-hidden flex flex-col items-center justify-center transition'
              >
                <img
                  src={card}
                  alt={`Card ${idx + 1}`}
                  className='w-full h-[15rem] object-cover'
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <IntegrateModal
        isOpen={showIntegrate}
        onClose={() => !loading && setShowIntegrate(false)}
        onConfirm={handleConnectIntegration}
        integrationName={integration.name}
        logo={integration.logo}
        loading={loading}
      />

      <DisconnectModal
        isOpen={showDisconnect}
        onClose={() => !loading && setShowDisconnect(false)}
        onConfirm={handleDisconnectIntegration}
        integrationName={integration.name}
        logo={integration.logo}
        loading={loading}
      />

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default IntegrationDetails;